import { CurrencyAmount, Percent, Token } from '@kodiak-finance/sdk-core'
import XKDK_ABI from 'abis/XKDK.json'
import { ButtonError } from 'components/Button'
import { LightCard, OutlineCard } from 'components/Card'
import Column, { AutoColumn } from 'components/Column'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import { AutoRow, RowBetween } from 'components/Row'
import useStepWithNext from 'components/StepLine/context/hooks/useStepWithNext'
import StepLineModal from 'components/StepLine/StepLineModal'
import { KDK_ADDRESSES, XKDK_ADDRESSES } from 'constants/addresses'
import { useToken } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import useStaking from 'hooks/useStaking'
import { useWriteContract } from 'hooks/useTransaction'
import useWaitForTransactionReceipt from 'hooks/useWaitForTransactionReceipt'
import { useTokenBalance } from 'lib/hooks/useCurrencyBalance'
import { formatSwapAmount } from 'lib/utils/formatAmount'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { useMemo, useState } from 'react'
import { Link, Text } from 'rebass'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import styled, { useTheme } from 'styled-components'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import type { Abi } from 'viem'

import { REFETCH_POLICY_FAST } from '../../config/RefetchPolicies'
import RedeemStep from './RedeemStep'
import RedeemWarning from './RedeemWarning'
import { TransactionInfoText } from './styleds'

const StyledController = styled(OutlineCard)<{ active?: boolean; pulsing?: boolean; disabled?: boolean }>`
  border-color: ${({ active, theme }) => (active ? theme.deprecated_blue1 : theme.cardBorderPrimary)};
  border-radius: 8px;
  height: 32px;
  width: 32px;

  background: ${({ theme }) => theme.cardPrimary};
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ disabled, theme }) =>
    !disabled
      ? `
    :hover {
      border-color: ${theme.deprecated_blue1};
      background: ${theme.deprecated_blue1};
    }
    cursor: pointer;
  `
      : `
    opacity: 0.5;
  `}

  overflow: hidden;

  user-select: none;
`

const Wrapper = styled(RowBetween)`
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: start;
    gap: 8px;
  }
`

const AutoRowAdaptive = styled(AutoRow)`
  width: fit-content;
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`

const AutoColumnAdaptive = styled(AutoColumn)`
  @media (max-width: 768px) {
    flex-direction: row;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
`

type RedeemDateSelectorProps = {
  min?: number
  max?: number
  current?: number
  onChange?: (current: number) => void
  incrementBy?: number
  text?: string
}
export const RedeemDateSelector = ({
  min,
  max,
  current,
  onChange,
  incrementBy = 15,
  text = 'Redeem duration',
}: RedeemDateSelectorProps) => {
  const { months, days } = useMemo(() => {
    if (!current) return {}
    const days = Math.floor(current / 86400)
    const months = Math.floor(days / 30)
    const remainingDays = days % 30

    return { months, days: remainingDays }
  }, [current])
  const theme = useTheme()

  const increment = () => {
    if (!onChange || !max) return
    const newValue = (current || 0) + 86400 * incrementBy
    if (newValue > max) return onChange(max)
    onChange(newValue)
  }

  const decrement = () => {
    if (!onChange || !min) return
    const newValue = (current || 0) - 86400 * incrementBy
    if (newValue < min) return onChange(min)
    onChange(newValue)
  }

  return (
    <Wrapper>
      <AutoColumnAdaptive>
        <Text fontSize={18} fontWeight={600} wrap="nowrap" style={{ textWrap: 'nowrap' }}>
          {text}
        </Text>
        <Link onClick={() => onChange && max && onChange(max)}>
          <Text fontSize={14}>Set max</Text>
        </Link>
      </AutoColumnAdaptive>
      <AutoRowAdaptive gap="8px">
        <StyledController onClick={decrement}>-</StyledController>
        <Column>
          <div>
            <Text fontSize={14} fontWeight={500} color={theme.textSecondary}>
              Months
            </Text>
          </div>
          <div>
            <Text fontSize={14} textAlign="center" fontWeight={500} color={theme.textPrimary}>
              {months || 0}
            </Text>
          </div>
        </Column>
        <Column>
          <div>
            <Text fontSize={14} fontWeight={500} color={theme.textSecondary}>
              Days
            </Text>
          </div>
          <div>
            <Text fontSize={14} textAlign="center" fontWeight={500} color={theme.textPrimary}>
              {days || 0}
            </Text>
          </div>
        </Column>
        <StyledController onClick={increment}>+</StyledController>
      </AutoRowAdaptive>
    </Wrapper>
  )
}

const RedeemView = () => {
  const [inputKdk, setInputKdk] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { chainId, account, wagmiPublicClient, isConnected } = useConnection()
  const xKdkAddress = XKDK_ADDRESSES[chainId]
  const xKdkToken = useToken(xKdkAddress)
  const kdkAddress = KDK_ADDRESSES[chainId]
  const kdkToken = useToken(kdkAddress)

  const amount = useMemo(() => {
    return tryParseCurrencyAmount(inputKdk, xKdkToken || undefined)
  }, [inputKdk, xKdkToken])
  const { minRedeemDuration, maxRedeemDuration } = useStaking()
  const [attemptingTxn, setAttemptingTxn] = useState(false)
  const [hash, setHash] = useState('')
  const theme = useTheme()
  const balance = useTokenBalance(account, xKdkToken || undefined, REFETCH_POLICY_FAST)
  const [redeemDuration, setRedeemDuration] = useState(maxRedeemDuration)
  const { writeContractAsync } = useWriteContract()
  const addTransaction = useTransactionAdder()
  const waitForTransactionReceipt = useWaitForTransactionReceipt()

  const isNotEnoughBalance = useMemo(() => {
    if (!amount || !balance) return false
    return amount.greaterThan(balance)
  }, [amount, balance])

  const [kdkOutput, multiplier] = useMemo<[CurrencyAmount<Token> | undefined, number | undefined]>(() => {
    if (!amount || !redeemDuration || !minRedeemDuration || !maxRedeemDuration) return [undefined, undefined]
    const minAmount = amount.multiply(5).divide(10) // x0.5
    if (redeemDuration === maxRedeemDuration) {
      return [amount, undefined] // x1
    }
    const durationRange = maxRedeemDuration - minRedeemDuration
    const userDuration = redeemDuration - minRedeemDuration
    const bonusMultiplier = 0.5 + (userDuration / durationRange) * 0.5
    const finalMultiplier = Math.min(Math.max(bonusMultiplier, 0), 1)
    const output = amount.multiply(Math.floor(bonusMultiplier * 1e6)).divide(1e6)

    if (output.lessThan(minAmount)) {
      return [minAmount, finalMultiplier]
    }
    return [output, finalMultiplier]
  }, [amount, redeemDuration, minRedeemDuration, maxRedeemDuration])

  const diffPercentage = useMemo(() => {
    if (!kdkOutput || !amount) return undefined
    return new Percent(kdkOutput.subtract(amount).multiply(10000).quotient, amount.multiply(10000).quotient)
  }, [amount, kdkOutput])

  const onRedeem = async () => {
    if (!xKdkAddress || !amount || !redeemDuration || !wagmiPublicClient || !account) return
    const quotient = amount.quotient.toString()

    setAttemptingTxn(true)
    try {
      const tx = {
        address: xKdkAddress,
        abi: XKDK_ABI as Abi,
        functionName: 'redeem',
        args: [BigInt(quotient), BigInt(redeemDuration)],
        account,
      }
      const gas = await wagmiPublicClient.estimateContractGas(tx)

      const txHash = await writeContractAsync({ ...tx, gas: calculateGasMargin(gas) })

      setHash(txHash)
      addTransaction(
        { hash: txHash },
        {
          type: TransactionType.STAKE_REDEEM,
          amount: `${amount?.toSignificant(6)} ${amount?.currency?.symbol}`,
        }
      )

      await waitForTransactionReceipt({
        hash: txHash,
      })
    } catch (e) {
      console.error('Error redeeming xKDK:', e)
    } finally {
      setAttemptingTxn(false)
    }
  }

  const redeemStep = useStepWithNext('redeem-step', RedeemStep, {
    amount,
    duration: redeemDuration,
    attemptingTxn,
    pendingText: (
      <>
        Redeeming {amount?.toSignificant(6)} {amount?.currency.symbol}
      </>
    ),
    hash,
    autoConfirm: true,
    transactionWaiting: true,
    onRedeem,
  })

  const handleConvert = () => {
    if (!amount || !amount.greaterThan('0')) return
    setHash('')
    setAttemptingTxn(false)
    setIsModalOpen(true)
  }

  const handleFinish = () => {
    setIsModalOpen(false)
    setHash('')
    setAttemptingTxn(false)
  }

  return (
    <>
      <StepLineModal steps={[redeemStep]} isOpen={isModalOpen} onDismiss={handleFinish} onFinish={handleFinish} />
      <CurrencyInputPanel
        // hideBalance={true}
        value={inputKdk}
        onUserInput={setInputKdk}
        onMax={() => setInputKdk(balance?.toExact() || '0')}
        showMaxButton={true}
        currency={xKdkToken}
        label="kdk"
        // onCurrencySelect={handleSelectCurrencyA}
        id="convert-kdk"
      />
      <LightCard>
        <RowBetween>
          <TransactionInfoText>You will receive</TransactionInfoText>
          <TransactionInfoText>
            {formatSwapAmount(kdkOutput) || '-'} KDK{' '}
            {diffPercentage === undefined || diffPercentage?.equalTo('0') ? (
              ''
            ) : (
              <span style={{ color: theme.deprecated_error }}>({diffPercentage?.toSignificant(2)}%)</span>
            )}
          </TransactionInfoText>
        </RowBetween>
      </LightCard>
      <RedeemDateSelector
        min={minRedeemDuration}
        max={maxRedeemDuration}
        current={redeemDuration}
        onChange={setRedeemDuration}
      />
      {redeemDuration !== maxRedeemDuration && (
        <RedeemWarning input={amount} output={kdkOutput} multiplier={multiplier} />
      )}

      <ButtonError
        error={redeemDuration !== maxRedeemDuration && multiplier !== undefined && multiplier < 0.8}
        disabled={!amount || !amount.greaterThan('0') || isNotEnoughBalance || attemptingTxn || !isConnected}
        onClick={handleConvert}
      >
        {isNotEnoughBalance ? 'Insufficient xKDK balance' : 'Redeem'}
      </ButtonError>
    </>
  )
}

export default RedeemView
